import React, { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import fetch, { resumeSessionExpiry, SESSION_LOST_EVENT, suspendSessionExpiry } from "lib/fetch";
import { fetchCsrfToken, useFresh, useLogin, useLogout, useSelectRole } from "hooks/authentication";
import type { AuthData } from "types/authentication";
import type { ProviderServiceRef } from "types/service";
import Acl from "./Acl";
import { Category } from "types/category";
import { ROUTES } from "routes";
import { hardResetServiceWorkers } from "serviceWorker";

interface Client {
    id: string;
    company_id?: string;
    company_name: string;
    company_code: string;
    company_percentage: string;
    has_pet: string;
    pet_type: string[];
    services: Array<Category>;
    birthday: string;
    comment?: string;
    rating: { average: number; count: number };
    code_cesu?: string;
    num_cesu?: string;
    account_name?: string;
    iban?: string;
    bic?: string;
}

interface Company {
    id: string;
    name: string;
    code: string;
    siren: string;
    limit: string;
    nb_employees: string;
    function: string;
    percentage: string;
    signed_date: string;
}

interface Provider {
    id: string;
    birthday: string;
    csp: string;
    identity_document_id: string;
    kbis_id: string;
    account_name: string;
    iban: string;
    bic: string;
    type: string;
    siret: string;
    legal_status: string;
    social_capital: string;
    has_tva: string;
    tva_number: string;
    tva_rate: number;
    services: Array<string>;
    provider_services: Array<ProviderServiceRef>;
    travel_range: number;
    description: string;
    rating: { average: number; count: number };
    has_cesu?: string;
    num_cesu?: string;
    nir?: string;
    stripe_id?: string;
}

interface Resource {
    client: Array<Client>;
    company: Array<Company>;
    provider: Array<Provider>;
}

interface Account {
    user: {
        id: string;
        gender: string;
        first_name: string;
        last_name: string;
        birth_name: string;
        phone: string;
        username: string;
        email: string;
        language_code: string;
        email_verified_at: string;
        roles: Array<string>;
        email_alerts: Array<string>;
        notification_alerts: Array<string>;
        address: string;
        house_number: string;
        street: string;
        postcode: string;
        city: string;
        commune_code: string;
        coordinates: {
            lat: Number;
            lon: Number;
        };
        picture_id?: string;
        currentRole?: string;
        status: string;
        country_birth_code?: string;
        department_birth_code?: string;
        commune_code_birth?: string;
        city_birth?: string;
        dataleon_qrcode?: string;
        dataleon_link?: string;
        dataleon_state?: string;
        dataleon_status?: string;
        dataleon_task_id?: string;
    };
    acl: Acl;
    resources?: Resource;
}

export interface AuthenticationProviderInterface extends Readonly<{}> {
    isLoading: Boolean;
    isLoggedIn: Boolean;
    account: Account | null;
    currentRole: string | null;
    login: (authInfo: { username: string; password: string }, redirectTo?: string) => Promise<void>;
    logout: () => Promise<void>;
    isAllowed: (capability: string) => boolean;
    filterAllowed: (actions: HashMap<unknown>, acl: HashMap<string>) => HashMap<unknown>;
    updateAccount: () => Promise<void>;
    selectRole: (roleId: string, redirectTo?: string) => Promise<void>;
    displaySelectRole: () => boolean | undefined;
}

export const ACCOUNT_KEY: string = "account";

type AuthDataWithResources = AuthData & { resources?: Resource };

function getDefaultPathAfterAuth(accountData: AuthData, roleId?: string): string {
    const role =
        roleId ??
        accountData.user.currentRole ??
        (accountData.user.roles.length === 1 ? accountData.user.roles[0] : "");

    if (!role && accountData.user.roles.length > 1) {
        return ROUTES.HOME;
    }

    switch (role) {
        case "provider":
            return ROUTES.ACCOUNT_PROFILE;
        case "admin":
            return ROUTES.ADMIN;
        case "client":
            return ROUTES.MY_ADS;
        default:
            return ROUTES.ACCOUNT;
    }
}

function storeDataToAccount(data: AuthDataWithResources, resetRole: boolean = false): Account {
    const account: Account = {
        user: data.user as unknown as Account["user"],
        acl: new Acl(),
        resources: data.resources,
    };
    if (resetRole) {
        delete account.user.currentRole;
    }

    data.capabilities.forEach((capability) => {
        account.acl.allow(capability);
    });

    return account;
}

function getAccount(): Account | null {
    if (fetch.sessionIsExpired()) {
        return null;
    }
    try {
        const account = localStorage.getItem(ACCOUNT_KEY);
        if (account) {
            return storeDataToAccount(JSON.parse(account));
        }
        return null;
    } catch {
        return null;
    }
}

function isLoggedIn(): boolean {
    return !!getAccount() && !fetch.sessionIsExpired();
}

const AuthContext = React.createContext<AuthenticationProviderInterface>({} as AuthenticationProviderInterface);

const AuthProvider: React.FC<PropsWithChildren<Readonly<{}>>> = ({ children }) => {
    const queryClient = useQueryClient();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isLoggedInState, setIsLoggedInState] = useState<boolean>(false);
    const [account, setAccount] = useState<Account | null>(null);
    const [currentRole, setCurrentRole] = useState<string | null>(null);
    const freshQuery = useFresh(false);
    const loginMutation = useLogin();
    const logoutMutation = useLogout();
    const selectRoleMutation = useSelectRole();

    const getRoleIfUnique = useCallback((inputAccount: Account | AuthData) => {
        return inputAccount.user.roles.length === 1 ? inputAccount.user.roles[0] : null;
    }, []);

    const isAllowed = useCallback(
        (capability: string) => {
            return !!account && account.acl.isAllowed(capability);
        },
        [account],
    );

    const filterAllowed = useCallback(
        (actions: HashMap<unknown>, acl: HashMap<string>) => {
            const actionsAllowed: HashMap<unknown> = {};
            for (const action in actions) {
                if (!acl[action]) {
                    actionsAllowed[action] = actions[action];
                } else if (isAllowed(acl[action])) {
                    actionsAllowed[action] = actions[action];
                }
            }
            return actionsAllowed;
        },
        [isAllowed],
    );

    const logout = useCallback(async () => {
        try {
            await logoutMutation.mutateAsync();
        } finally {
            localStorage.removeItem(ACCOUNT_KEY);
            fetch.clearSession();
            queryClient.clear();
            setIsLoggedInState(false);
            setAccount(null);
            setCurrentRole(null);
            // Ensure we never keep stale cached API responses across account switches
            // (e.g. from older service worker versions).
            try {
                await hardResetServiceWorkers();
            } catch {
                // Best-effort; auth state has already been cleared locally.
            }
            window.location.href = ROUTES.HOME;
        }
    }, [logoutMutation, queryClient]);

    const updateAccount = useCallback(async () => {
        // refetch() does not throw on HTTP errors — check result.error explicitly.
        const result = await freshQuery.refetch();
        const data = result.data as { error?: unknown; user?: unknown } | undefined;

        if (result.error || data?.error !== undefined) {
            await logout();
            return;
        }

        if (data?.user !== undefined) {
            const accountData = Object.assign({}, data) as AuthDataWithResources;
            localStorage.setItem(ACCOUNT_KEY, JSON.stringify(accountData));
            const nextRole = currentRole == null ? accountData.user.currentRole : currentRole;
            setIsLoggedInState(true);
            setAccount(storeDataToAccount(accountData));
            setCurrentRole(nextRole ?? getRoleIfUnique(accountData));
            return;
        }

        // Session cookie present in localStorage but server did not return an account.
        await logout();
    }, [currentRole, freshQuery, getRoleIfUnique, logout]);

    const login = useCallback(
        async ({ username, password }: { username: string; password: string }, redirectTo?: string) => {
            // Abort calls still using the previous session id. Their responses must not
            // 401 the new session or race the regenerated cookie.
            suspendSessionExpiry();
            let navigated = false;
            try {
                await queryClient.cancelQueries();
                await fetchCsrfToken();
                queryClient.clear();
                const data = await loginMutation.mutateAsync({ username, password });
                const accountData = Object.assign({}, data);
                localStorage.setItem(ACCOUNT_KEY, JSON.stringify(accountData));

                const safe =
                    redirectTo && redirectTo.startsWith("/") && !redirectTo.startsWith("//")
                        ? redirectTo
                        : null;
                // Hard-reload after login so cookies, React Query and in-memory auth state
                // stay in sync.
                navigated = true;
                window.location.href = safe ?? getDefaultPathAfterAuth(accountData);
            } finally {
                if (!navigated) {
                    resumeSessionExpiry();
                }
            }
        },
        [loginMutation, queryClient],
    );

    const selectRole = useCallback(
        async (roleId: string, redirectTo?: string) => {
            setIsLoading(true);
            suspendSessionExpiry();
            let navigated = false;
            try {
                await queryClient.cancelQueries();
                const data = await selectRoleMutation.mutateAsync(roleId);
                const accountData = Object.assign({}, data) as AuthDataWithResources;
                localStorage.setItem(ACCOUNT_KEY, JSON.stringify(accountData));
                queryClient.clear();
                const safe =
                    redirectTo && redirectTo.startsWith("/") && !redirectTo.startsWith("//")
                        ? redirectTo
                        : null;
                navigated = true;
                window.location.href = safe ?? getDefaultPathAfterAuth(accountData, roleId);
            } finally {
                if (!navigated) {
                    resumeSessionExpiry();
                }
                setIsLoading(false);
            }
        },
        [queryClient, selectRoleMutation],
    );

    const displaySelectRole = useCallback(() => {
        return isLoggedInState && !!account?.user.roles && account.user.roles.length !== 1 && !account.user.currentRole;
    }, [account, isLoggedInState]);

    useEffect(() => {
        const init = async () => {
            try {
                if (!isLoggedIn()) {
                    return;
                }
                await updateAccount();
            } catch {
                await logout();
            } finally {
                setIsLoading(false);
            }
        };

        void init();
        // Run once on mount: including callback dependencies here triggers
        // repeated auth refresh calls because those callbacks are re-created.
    }, []);

    const logoutRef = useRef(logout);
    logoutRef.current = logout;

    useEffect(() => {
        const onSessionLost = () => {
            if (!localStorage.getItem(ACCOUNT_KEY)) {
                return;
            }
            void logoutRef.current();
        };

        window.addEventListener(SESSION_LOST_EVENT, onSessionLost);
        return () => window.removeEventListener(SESSION_LOST_EVENT, onSessionLost);
    }, []);

    const value = useMemo<AuthenticationProviderInterface>(
        () => ({
            isLoading,
            isLoggedIn: isLoggedInState,
            account,
            currentRole,
            login,
            logout,
            isAllowed,
            filterAllowed,
            updateAccount,
            selectRole,
            displaySelectRole,
        }),
        [
            account,
            currentRole,
            displaySelectRole,
            filterAllowed,
            isAllowed,
            isLoading,
            isLoggedInState,
            login,
            logout,
            selectRole,
            updateAccount,
        ],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const Consumer = AuthContext.Consumer;

export { AuthProvider as Provider, Consumer, AuthContext as Context };
